import React, { useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  onClose: (id: string) => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ id, type, message, onClose, duration = 3000 }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-success-400" />;
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-error-400" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-warning-400" />;
      case 'info':
        return <InformationCircleIcon className="h-5 w-5 text-info-400" />;
    }
  };

  const getStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800';
      case 'error':
        return 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-800';
      case 'warning':
        return 'bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-800';
      case 'info':
        return 'bg-info-50 dark:bg-info-900/20 border-info-200 dark:border-info-800';
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'success':
        return 'text-success-800 dark:text-success-200';
      case 'error':
        return 'text-error-800 dark:text-error-200';
      case 'warning':
        return 'text-warning-800 dark:text-warning-200';
      case 'info':
        return 'text-info-800 dark:text-info-200';
    }
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg transform transition-all duration-300 ${getStyles()} animate-slide-in-right`}
    >
      {getIcon()}
      <p className={`flex-1 text-sm font-medium ${getTextColor()}`}>{message}</p>
      <button
        onClick={() => onClose(id)}
        className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
      >
        <XMarkIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Array<{ id: string; type: ToastType; message: string }>;
  removeToast: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          id={toast.id}
          type={toast.type}
          message={toast.message}
          onClose={removeToast}
        />
      ))}
    </div>
  );
};

export default Toast;